(() => {
  const cartUrl = () => window.routes?.cart_url || `${window.Shopify?.routes?.root || '/'}cart`;

  class EditionProduct extends HTMLElement {
    connectedCallback() {
      if (this.bound) return;
      this.bound = true;
      this.data = JSON.parse(this.querySelector('[data-edition-product-data]').textContent);
      this.form = this.querySelector('[data-edition-product-form]');
      this.quantity = this.querySelector('[data-edition-quantity]');
      this.submitButton = this.querySelector('[data-edition-add]');
      this.submitLabel = this.querySelector('[data-edition-add-label]');
      this.error = this.querySelector('[data-edition-error]');
      this.status = this.querySelector('[data-edition-status]');
      this.plan = this.querySelector('[data-edition-plan]');
      this.busy = false;
      this.form.addEventListener('submit', event => this.addToCart(event));
      this.querySelectorAll('[data-edition-variant]').forEach(input => input.addEventListener('change', () => {
        this.error.hidden = true;
        this.renderVariant(true);
        this.showGallery('packaging');
      }));
      this.plan?.addEventListener('change', () => this.renderVariant(false));
      this.querySelectorAll('[data-edition-quantity-step]').forEach(button => button.addEventListener('click', () => {
        if (!this.quantity.value || !Number.isFinite(this.quantity.valueAsNumber)) this.quantity.value = this.quantity.min || '1';
        if (button.dataset.editionQuantityStep === '1') this.quantity.stepUp(); else this.quantity.stepDown();
        this.quantity.dispatchEvent(new Event('change', { bubbles: true }));
      }));
      this.querySelectorAll('[data-edition-gallery]').forEach(button => button.addEventListener('click', () => this.showGallery(button.dataset.editionGallery)));
      this.renderVariant(false);
    }

    selectedVariant() {
      const value = this.form.querySelector('input[name="id"]:checked')?.value || this.form.querySelector('input[name="id"][type="hidden"]')?.value;
      return this.data.variants.find(variant => String(variant.id) === value);
    }

    renderVariant(updateUrl) {
      const variant = this.selectedVariant();
      if (!variant) {
        this.submitButton.disabled = true;
        this.submitLabel.textContent = 'Unavailable';
        return;
      }
      this.currentVariant = variant;
      const planId = this.plan?.value;
      const allocation = variant.plans.find(plan => String(plan.id) === planId);
      const available = variant.available && (!planId || Boolean(allocation));
      this.querySelector('[data-edition-price]').textContent = allocation?.priceText || variant.priceText;
      const compare = this.querySelector('[data-edition-compare]');
      compare.hidden = Boolean(planId) || variant.comparePrice <= variant.price;
      compare.textContent = variant.compareText;
      this.submitButton.disabled = this.busy || !available;
      this.submitLabel.textContent = this.busy ? 'Adding…' : available ? 'Add to bag' : 'Sold out';
      this.quantity.min = String(variant.minimum);
      this.quantity.step = String(variant.increment);
      if (variant.maximum != null) this.quantity.max = String(variant.maximum); else this.quantity.removeAttribute('max');
      if (this.quantity.valueAsNumber < variant.minimum) this.quantity.value = String(variant.minimum);
      if (variant.maximum != null && this.quantity.valueAsNumber > variant.maximum) this.quantity.value = String(variant.maximum);

      if (this.dataset.isMb === 'true') {
        const title = variant.title.toLowerCase();
        const label = title.includes('strawberry') ? 'Strawberry Cream' : title.includes('apple') ? 'Apple Juice' : 'Lemon-Lime';
        const pouch = this.querySelector('[data-edition-pouch]');
        const sachet = this.querySelector('[data-edition-sachet]');
        pouch.src = variant.image;
        pouch.alt = `SPIKE MB-0.2 ${label} pouch`;
        sachet.src = variant.sachet;
        sachet.alt = `SPIKE MB-0.2 ${label} sachet`;
        this.style.setProperty('--edition-product-accent', title.includes('strawberry') ? '#eed7df' : title.includes('apple') ? '#dce5cd' : '#dce4a8');
      }
      if (updateUrl) {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', String(variant.id));
        window.history.replaceState({}, '', url);
      }
    }

    showGallery(mode) {
      const alternate = mode === 'alternate';
      this.querySelector('[data-edition-packaging]')?.toggleAttribute('hidden', alternate);
      this.querySelector('[data-edition-alternate]')?.toggleAttribute('hidden', !alternate);
      this.querySelector('[data-edition-image-link]')?.toggleAttribute('hidden', !alternate);
      this.querySelector('[data-edition-main-media]').classList.toggle('edition-pdp-stage--alternate', alternate);
      this.querySelectorAll('[data-edition-gallery]').forEach(button => button.setAttribute('aria-pressed', String(button.dataset.editionGallery === mode)));
    }

    async addToCart(event) {
      event.preventDefault();
      if (this.busy || !this.form.reportValidity()) return;
      const variant = this.selectedVariant();
      if (!variant?.available) return;
      this.busy = true;
      this.error.hidden = true;
      this.submitButton.setAttribute('aria-busy', 'true');
      this.renderVariant(false);
      const body = new FormData(this.form);
      const drawer = document.querySelector('cart-drawer');
      if (drawer?.getSectionsToRender) {
        body.set('sections', drawer.getSectionsToRender().map(section => section.id).join(','));
        body.set('sections_url', window.location.pathname);
        drawer.setActiveElement(this.submitButton);
      }
      let added = false;
      try {
        const addUrl = window.routes?.cart_add_url || `${window.Shopify?.routes?.root || '/'}cart/add`;
        const response = await fetch(addUrl.endsWith('.js') ? addUrl : `${addUrl}.js`, {
          method: 'POST', headers: { Accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' }, body
        });
        const result = await response.json();
        if (!response.ok || result.status) throw new Error(result.description || result.message || 'Unable to add this item. Please try again.');
        added = true;
        this.status.textContent = `${this.data.title} added to your bag.`;
        if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          publish(PUB_SUB_EVENTS.cartUpdate, { source: 'edition-product', productVariantId: variant.id, cartData: result });
        }
        const sectionsReady = drawer?.getSectionsToRender && drawer.getSectionsToRender().every(section => typeof result.sections?.[section.id] === 'string');
        if (sectionsReady) {
          drawer.classList.remove('is-empty');
          drawer.renderContents(result);
        } else {
          window.location.assign(cartUrl());
        }
      } catch (error) {
        if (added) {
          window.location.assign(cartUrl());
          return;
        }
        this.error.textContent = error.message || 'Unable to add this item. Please try again.';
        this.error.hidden = false;
      } finally {
        this.busy = false;
        this.submitButton.removeAttribute('aria-busy');
        this.renderVariant(false);
      }
    }
  }

  if (!customElements.get('edition-product')) customElements.define('edition-product', EditionProduct);
  document.addEventListener('keydown', event => {
    if (event.key === 'Escape') document.querySelectorAll('.edition-menu[open]').forEach(menu => { menu.open = false; menu.querySelector('summary').focus(); });
  });
  document.addEventListener('click', event => {
    document.querySelectorAll('.edition-menu[open]').forEach(menu => { if (!menu.contains(event.target)) menu.open = false; });
  });
  const bindLocalization = () => {
    const form = document.getElementById('EditionLocalization');
    if (!form || form.dataset.editionBound) return;
    form.dataset.editionBound = 'true';
    const changeMarket = event => {
      const select = form.querySelector('[data-edition-country]');
      const selected = Array.from(select.options).find(option => option.value === select.value);
      if (!selected?.dataset.currency) return;
      event.preventDefault();
      const url = new URL(window.location.href);
      url.searchParams.set('country', selected.value);
      url.searchParams.set('currency', selected.dataset.currency);
      url.hash = '';
      window.location.assign(url);
    };
    form.addEventListener('submit', changeMarket);
    form.querySelector('button[type="submit"]').addEventListener('click', changeMarket);
  };
  bindLocalization();
  document.addEventListener('shopify:section:load', bindLocalization);
})();
